import React, { useEffect, useState } from "react";
import Navbar from "../NavBar";
import Footer from "../Footer";
import Seo from "../Seo";
import { breadcrumbJsonLd } from "../../seoSchema";
import "../../styles/Playhouse.css";
import { Link } from "react-router-dom";
import { initializeApp } from "firebase/app";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { PLAYHOUSE_LISTING_CARD_META } from "../../playhouseListingShared";
import { CmsImage } from "../../firestoreMedia";

const firebaseConfig = {
	apiKey: process.env.REACT_APP_FIRESTORE_API_KEY,
	authDomain: "wishuponaplayhouse.firebaseapp.com",
	projectId: "wishuponaplayhouse",
	storageBucket: "wishuponaplayhouse.firebasestorage.app",
	messagingSenderId: "513377368862",
	appId: "1:513377368862:web:eadda2a0b7bb141ffa670f",
	measurementId: "G-NZ116LS0Q1",
};

const app = initializeApp(firebaseConfig);
const firestore = getFirestore(app);


function Playhouse() {
	const [cottageTitle, setCottageTitle] = useState("");
	const [cottageParagraph, setCottageParagraph] = useState("");

	const [ranchTitle, setRanchTitle] = useState("");
	const [ranchParagraph, setRanchParagraph] = useState("");

	const [sweetshopTitle, setSweetshopTitle] = useState("");
	const [sweetshopParagraph, setSweetshopParagraph] = useState("");

	const [clubhouseTitle, setClubhouseTitle] = useState("");
	const [clubhouseParagraph, setClubhouseParagraph] = useState("");

	const [cardHeroImages, setCardHeroImages] = useState({
		cottage: null,
		ranch: null,
		sweetshop: null,
		clubhouse: null,
	});

	const [heroHeading, setHeroHeading] = useState("Our Playhouses");
	const [heroSubtitle, setHeroSubtitle] = useState("Each playhouse is handcrafted and built on-site — no kits, no assembly required.");
	const [listingContentReady, setListingContentReady] = useState(false);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const listingRef = doc(firestore, "Pages", "PlayhousesPage");
				const heroRefs = PLAYHOUSE_LISTING_CARD_META.map(({ docId }) =>
					getDoc(doc(firestore, "Pages", docId))
				);
				const [listingSnap, ...heroSnaps] = await Promise.all([getDoc(listingRef), ...heroRefs]);

				if (listingSnap.exists()) {
					const data = listingSnap.data();
					setCottageTitle(data.cottageTitle);
					setCottageParagraph(data.cottageParagraph);
					setRanchTitle(data.ranchTitle);
					setRanchParagraph(data.ranchParagraph);
					setSweetshopTitle(data.sweetshopTitle);
					setSweetshopParagraph(data.sweetshopParagraph);
					setClubhouseTitle(data.clubhouseTitle);
					setClubhouseParagraph(data.clubhouseParagraph);
					if (data.heroHeading) setHeroHeading(data.heroHeading);
					if (data.heroSubtitle) setHeroSubtitle(data.heroSubtitle);
				}

				const nextHeroes = {};
				PLAYHOUSE_LISTING_CARD_META.forEach(({ key }, i) => {
					const snap = heroSnaps[i];
					nextHeroes[key] = snap.exists() ? snap.data().heroImage || null : null;
				});
				setCardHeroImages(nextHeroes);
			} catch (error) {
				console.error("Error fetching playhouse content: ", error);
			} finally {
				setListingContentReady(true);
			}
		};

		fetchData();
	}, []);

	const fallbackByKey = Object.fromEntries(
		PLAYHOUSE_LISTING_CARD_META.map((c) => [c.key, c.fallback])
	);

	const playhouses = [
		{
			id: 1,
			name: "cottage",
			heroImage: cardHeroImages.cottage,
			imageFallback: fallbackByKey.cottage,
			title: cottageTitle,
			description: cottageParagraph,
			links: "/playhouses/cottage",
		},
		{
			id: 2,
			name: "ranch",
			heroImage: cardHeroImages.ranch,
			imageFallback: fallbackByKey.ranch,
			title: ranchTitle,
			description: ranchParagraph,
			links: "/playhouses/ranch",
		},
		{
			id: 3,
			name: "sweetshop",
			heroImage: cardHeroImages.sweetshop,
			imageFallback: fallbackByKey.sweetshop,
			title: sweetshopTitle,
			description: sweetshopParagraph,
			links: "/playhouses/sweet-shoppe",
		},
		{
			id: 4,
			name: "clubhouse",
			heroImage: cardHeroImages.clubhouse,
			imageFallback: fallbackByKey.clubhouse,
			title: clubhouseTitle,
			description: clubhouseParagraph,
			links: "/playhouses/clubhouse",
		},
	];

	return (
		<div className="playhousesPage">
			<Seo
				title="Playhouse Collection — Ranch, Cottage, Clubhouse & Sweet Shoppe | Denver, CO"
				description="Explore our collection of custom children's playhouses: Ranch, Cottage, Clubhouse, Sweet Shoppe, and fully custom designs. Handcrafted on-site in Denver, Colorado."
				path="/playhouses"
				jsonLd={breadcrumbJsonLd([
					{ name: "Home", path: "/" },
					{ name: "Playhouses", path: "/playhouses" },
				])}
				jsonLdId="playhouses-breadcrumb"
			/>
			<Navbar />
	<div className="playhouses-hero">
		<h1>{heroHeading}</h1>
		<p>{heroSubtitle}</p>
	</div>
		<div className="playhousesContainer">
			{playhouses.map((playhouse) => (
				<Link to={playhouse.links} className="playhouseCard" key={playhouse.id}>
					<div className="playhouse-img-wrap">
						<CmsImage
							pending={!listingContentReady}
							image={playhouse.heroImage}
							fallback={playhouse.imageFallback}
							alt={`${playhouse.title} playhouse — Wish Upon A Playhouse, Denver Colorado`}
							loading="eager"
							fetchpriority={playhouse.id <= 2 ? "high" : "auto"}
							onError={(e) => {
								e.target.onerror = null;
								e.target.src = "/images/image-placeholder.png";
							}}
						/>
					<div className="playhouse-img-overlay">
						<span>
							View Details
							<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
								<path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
							</svg>
						</span>
					</div>
					</div>
					<h2 className="playhouse-card-title">{playhouse.title}</h2>
					<p className="playhouse-card-desc">{playhouse.description}</p>
					<div className="playhouse-card-cta">
						<span>Explore This Playhouse</span>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
					</div>
				</Link>
			))}
			</div>
			<Footer />
		</div>
	);
}

export default Playhouse;
